import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Logger from '../../../common/logger';
import {
    AdminGroup,
    useUpdateGroupDepositMutation,
} from '../../../api/group/groupAdminApiSlice';
import CurrencyAmountInput from '../../../components/CurrencyAmountInput';
import CustomButton from '../../../components/CustomButton';
import { getApiErrorStatus } from '../../../utils/apiError';
import { isValidAmountInput } from '../../../utils/amountValidation';
import CustomModal from '../../common/CustomModal';

interface Props {
    group: AdminGroup;
    onClose: () => void;
}

// Small per-row deposit editor (docs/plans/deposit-admin-group-config.md):
// one amount field, empty = clear the deposit (null). The backend also
// normalizes 0 -> null so "no deposit" has one representation.
const EditDepositModal = ({ group, onClose }: Props) => {
    const { t } = useTranslation();
    const [amount, setAmount] = useState(
        group.depositAmount != null ? String(group.depositAmount) : '',
    );
    const [formError, setFormError] = useState<string | null>(null);
    // Firefox reports non-numeric input on <input type="number"> as
    // value === '' with validity.badInput set — without this guard that
    // would silently pass as "empty = clear the deposit".
    const [hasBadInput, setHasBadInput] = useState(false);
    const [updateGroupDeposit, { isLoading }] = useUpdateGroupDepositMutation();

    // Validation derives from form state (not API data), per the edit-form rule.
    const trimmed = amount.trim();
    const isValid = !hasBadInput && isValidAmountInput(trimmed);

    // Inline validation feedback: the save button is disabled while invalid,
    // so the message must show as the user types (badInput leaves the value
    // empty, hence the extra check).
    const showValidation = !isValid && (trimmed.length > 0 || hasBadInput);

    const handleSave = async () => {
        if (!isValid) return;
        setFormError(null);
        try {
            await updateGroupDeposit({
                groupId: group.id,
                depositAmount: trimmed.length === 0 ? null : Number(trimmed),
            }).unwrap();
            onClose();
        } catch (err) {
            // Always show a localized message; raw backend messages are
            // English-only and would leak untranslated into the DE UI. 403 =
            // missing `edit:deposit-settings` — same message as the deposit modals.
            Logger('EditDepositModal').error(`Deposit update failed: ${JSON.stringify(err)}`);
            setFormError(
                getApiErrorStatus(err) === 403
                    ? t('deposits.errors.forbidden')
                    : t('services.deposits.edit_deposit.error'),
            );
        }
    };

    return (
        <CustomModal
            open={true}
            title={t('services.deposits.edit_deposit.title')}
            description={group.name ?? t('common.missing')}
            onClose={onClose}
            closeButton={
                <CustomButton title={t('common.cancel')} isFilled={false} onClick={onClose} />
            }
            saveButton={
                <CustomButton
                    title={t('common.save')}
                    loading={isLoading}
                    disabled={!isValid || isLoading}
                    onClick={handleSave}
                />
            }
        >
            <div className={'flex flex-col gap-1'}>
                <CurrencyAmountInput
                    id={'deposit-amount'}
                    label={t('services.deposits.edit_deposit.amount_label')}
                    value={amount}
                    autoFocus
                    onValueChange={(value, badInput) => {
                        setAmount(value);
                        setHasBadInput(badInput);
                        setFormError(null);
                    }}
                    hint={t('services.deposits.edit_deposit.amount_hint')}
                    validationMessage={
                        showValidation ? t('services.deposits.edit_deposit.validation') : null
                    }
                />
                {formError && <p className={'text-sm text-red-500'}>{formError}</p>}
            </div>
        </CustomModal>
    );
};

export default EditDepositModal;
